import * as React from 'react';

import {
  DeletedMessageLayout,
  DocumentMessageLayout,
  ImageMessageLayout,
  ImageWithTextMessageLayout,
  OfferMessageLayout,
  ImageAsDocumentMessageLayout,
  TextMessageLayout,
} from './components';
import { EMessageContentType } from '../../types/messageContentType';
import { EMessageType } from '../../types/messageType';
import { EUserColor } from '../../types/userName';

interface IChatMessageContentProps {
  contentType: EMessageContentType;
  messageType: EMessageType;
  isLastMessageInChain: boolean;
  userName: React.ReactNode | null;
  documentMessage: React.ReactNode | null;
  imageMessage: React.ReactNode | null;
  textMessage: React.ReactNode | null;
  errorSign: React.ReactNode | null;
  errorText: React.ReactNode | null;
  messageMeta: React.ReactNode | null;
  actionButtons: React.ReactNode | null;
  offerMessage: React.ReactNode | null;
  imageAsDocumentMessage: React.ReactNode | null;
  deletedMessage: React.ReactNode | null;
  userColor?: EUserColor;
  replyQuote?: React.ReactNode | null;
  highlighted?: boolean;
  onHighlightEnd?: VoidFunction;
}

export const ChatMessageContent = React.memo(
  React.forwardRef<HTMLDivElement, IChatMessageContentProps>(function ChatMessageContent(
    {
      contentType,
      messageType,
      isLastMessageInChain,
      userName,
      documentMessage,
      imageMessage,
      textMessage,
      errorSign,
      errorText,
      messageMeta,
      actionButtons,
      offerMessage,
      imageAsDocumentMessage,
      deletedMessage,
      userColor,
      replyQuote,
      highlighted,
      onHighlightEnd,
    },
    ref,
  ) {
    return (
      <>
        {contentType === EMessageContentType.Deleted && (
          <DeletedMessageLayout
            messageType={messageType}
            isLastMessageInChain={isLastMessageInChain}
            messageMeta={messageMeta}
            userColor={userColor}
            userName={userName}
            ref={ref}
            text={deletedMessage}
            highlighted={highlighted}
            onHighlightEnd={onHighlightEnd}
          />
        )}

        {contentType === EMessageContentType.Document && (
          <DocumentMessageLayout
            errorSign={errorSign}
            errorText={errorText}
            messageType={messageType}
            isLastMessageInChain={isLastMessageInChain}
            documentMessage={documentMessage}
            messageMeta={messageMeta}
            userColor={userColor}
            ref={ref}
            replyQuote={replyQuote}
            highlighted={highlighted}
            onHighlightEnd={onHighlightEnd}
          />
        )}

        {contentType === EMessageContentType.Image && (
          <ImageMessageLayout
            errorSign={errorSign}
            errorText={errorText}
            messageType={messageType}
            isLastMessageInChain={isLastMessageInChain}
            imageMessage={imageMessage}
            messageMeta={messageMeta}
            userColor={userColor}
            ref={ref}
            replyQuote={replyQuote}
            highlighted={highlighted}
            onHighlightEnd={onHighlightEnd}
          />
        )}

        {contentType === EMessageContentType.ImageWithText && (
          <ImageWithTextMessageLayout
            errorSign={errorSign}
            errorText={errorText}
            messageType={messageType}
            isLastMessageInChain={isLastMessageInChain}
            imageMessage={imageMessage}
            textMessage={textMessage}
            messageMeta={messageMeta}
            actionButtons={actionButtons}
            userColor={userColor}
            ref={ref}
            replyQuote={replyQuote}
            highlighted={highlighted}
            onHighlightEnd={onHighlightEnd}
          />
        )}

        {contentType === EMessageContentType.Offer && (
          <OfferMessageLayout
            errorSign={errorSign}
            errorText={errorText}
            messageType={messageType}
            isLastMessageInChain={isLastMessageInChain}
            offerMessage={offerMessage}
            messageMeta={messageMeta}
            actionButtons={actionButtons}
            userColor={userColor}
            ref={ref}
            replyQuote={replyQuote}
            highlighted={highlighted}
            onHighlightEnd={onHighlightEnd}
          />
        )}

        {contentType === EMessageContentType.ImageAsDocument && (
          <ImageAsDocumentMessageLayout
            errorSign={errorSign}
            errorText={errorText}
            messageType={messageType}
            isLastMessageInChain={isLastMessageInChain}
            imageAsDocumentMessage={imageAsDocumentMessage}
            messageMeta={messageMeta}
            userColor={userColor}
            ref={ref}
            replyQuote={replyQuote}
            highlighted={highlighted}
            onHighlightEnd={onHighlightEnd}
          />
        )}

        {contentType === EMessageContentType.Text && (
          <TextMessageLayout
            errorSign={errorSign}
            errorText={errorText}
            messageType={messageType}
            isLastMessageInChain={isLastMessageInChain}
            userName={userName}
            textMessage={textMessage}
            messageMeta={messageMeta}
            actionButtons={actionButtons}
            userColor={userColor}
            ref={ref}
            replyQuote={replyQuote}
            highlighted={highlighted}
            onHighlightEnd={onHighlightEnd}
          />
        )}
      </>
    );
  }),
);
